import { useState, useEffect } from "react";
import { Close } from "components/assets/Icons";
import styles from "./AICallPreviewModal.module.scss";
import quoteService from "services/quote.service";

const AICallPreviewModal = ({
  data,
  loading,
  onClose,
  onSave
}) => {
  const [formData, setFormData] = useState({
    driver_name: '',
    suggested_to_number: '',
    pickup_date: '',
    delivery_date: '',
    pickup_city: '',
    pickup_state: '',
    pickup_area: '',
    delivery_city: '',
    delivery_state: '',
    delivery_area: '',
    pieces: '',
    weight: '',
    dimensions: '',
    special_equipment: '',
    total_miles: '',
    dispatcher_name: '',
    empty_miles: ''
  });
  const [callLoading, setCallLoading] = useState(false);

  // Array of fields that should only accept numeric input
  const NUMERIC_FIELDS = ['pieces', 'weight', 'total_miles', 'empty_miles'];

  // Update form data when new data is received
  useEffect(() => {
    if (data?.dynamic_variables) {
      setFormData({
        ...data.dynamic_variables,
        suggested_to_number: data.suggested_to_number || ''
      });
    } else if (data) {
      // Handle case where data structure might be different
      // Check if the data itself contains the fields
      if (data.driver_name !== undefined || data.pickup_date !== undefined) {
        setFormData(data);
      }
    }
  }, [data]);

  // Validation function for numeric fields
  const validateNumericInput = (value) => {
    // Allow empty string, digits, and decimal point for fields like Target Rate
    const numericRegex = /^[0-9]*\.?[0-9]*$/;
    return numericRegex.test(value);
  };

  const handleInputChange = (field, value) => {
    // Apply validation for numeric fields
    if (NUMERIC_FIELDS.includes(field)) {
      if (!validateNumericInput(value)) {
        return; // Prevent invalid input
      }
    }

    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSave = () => {
    if (onSave) {
      onSave(formData);
    }
  };

  const handleCall = async () => {
    try {
      setCallLoading(true);

      // Extract dynamic_variables (exclude suggested_to_number)
      const { suggested_to_number, ...dynamicVariables } = formData;

      // Make the API call
      await quoteService.createCall(
        data.rfq_id,
        data.truck_info.id,
        formData.suggested_to_number,
        dynamicVariables
      );

      // Close modal on success
      onClose();
    } catch (error) {
      console.error('Error creating call:', error);
      // You could add error handling here (show error message, etc.)
    } finally {
      setCallLoading(false);
    }
  };

  if (loading) {
    return (
      <>
        <div className={styles.header}>
          <h2>AI Call Preview</h2>
          <button className={styles.closeButton} onClick={onClose}>
            <Close />
          </button>
        </div>
        <div className={styles.content}>
          <div className={styles.loading}>Loading call preview...</div>
        </div>
      </>
    );
  }

  // Handle case where there's no data yet
  if (!data) {
    return (
      <>
        <div className={styles.header}>
          <h2>AI Call Preview</h2>
          <button className={styles.closeButton} onClick={onClose}>
            <Close />
          </button>
        </div>
        <div className={styles.content}>
          <div className={styles.loading}>No data available</div>
        </div>
      </>
    );
  }

  return (
    <>
      <div className={styles.header}>
        <h2>AI Call Preview</h2>
        <button className={styles.closeButton} onClick={onClose}>
          <Close />
        </button>
      </div>
      <div className={styles.content}>
        <div className={styles.form}>
          <div className={styles.section}>
            <h3>Driver Information</h3>
            <div className={styles.formGroup}>
              <label>Driver Name:</label>
              <input
                type="text"
                value={formData.driver_name || ''}
                onChange={(e) => handleInputChange('driver_name', e.target.value)}
                className={styles.input}
              />
            </div>
            <div className={styles.formGroup}>
              <label>Driver Phone:</label>
              <input
                type="tel"
                value={formData.suggested_to_number || ''}
                onChange={(e) => handleInputChange('suggested_to_number', e.target.value)}
                className={styles.input}
              />
            </div>
          </div>

          <div className={styles.section}>
            <h3>Schedule</h3>
            <div className={styles.formGroup}>
              <label>Pickup Date:</label>
              <input
                type="text"
                value={formData.pickup_date || ''}
                onChange={(e) => handleInputChange('pickup_date', e.target.value)}
                className={styles.input}
              />
            </div>
            <div className={styles.formGroup}>
              <label>Delivery Date:</label>
              <input
                type="text"
                value={formData.delivery_date || ''}
                onChange={(e) => handleInputChange('delivery_date', e.target.value)}
                className={styles.input}
              />
            </div>
          </div>

          <div className={styles.section}>
            <h3>Pickup Location</h3>
            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label>City:</label>
                <input
                  type="text"
                  value={formData.pickup_city || ''}
                  onChange={(e) => handleInputChange('pickup_city', e.target.value)}
                  className={styles.input}
                />
              </div>
              <div className={styles.formGroup}>
                <label>State:</label>
                <input
                  type="text"
                  value={formData.pickup_state || ''}
                  onChange={(e) => handleInputChange('pickup_state', e.target.value)}
                  className={styles.input}
                />
              </div>
            </div>
            <div className={styles.formGroup}>
              <label>Pickup Area:</label>
              <input
                type="text"
                value={formData.pickup_area || ''}
                onChange={(e) => handleInputChange('pickup_area', e.target.value)}
                className={styles.input}
              />
            </div>
          </div>

          <div className={styles.section}>
            <h3>Delivery Location</h3>
            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label>City:</label>
                <input
                  type="text"
                  value={formData.delivery_city || ''}
                  onChange={(e) => handleInputChange('delivery_city', e.target.value)}
                  className={styles.input}
                />
              </div>
              <div className={styles.formGroup}>
                <label>State:</label>
                <input
                  type="text"
                  value={formData.delivery_state || ''}
                  onChange={(e) => handleInputChange('delivery_state', e.target.value)}
                  className={styles.input}
                />
              </div>
            </div>
            <div className={styles.formGroup}>
              <label>Delivery Area:</label>
              <input
                type="text"
                value={formData.delivery_area || ''}
                onChange={(e) => handleInputChange('delivery_area', e.target.value)}
                className={styles.input}
              />
            </div>
          </div>

          <div className={`${styles.section} ${styles.fullWidth}`}>
            <h3>Load Details & Dispatcher</h3>
            <div className={styles.threeColumnRow}>
              <div className={styles.column}>
                <div className={styles.formGroup}>
                  <label>Pieces:</label>
                  <input
                    type="text"
                    inputMode="numeric"
                    value={formData.pieces || ''}
                    onChange={(e) => handleInputChange('pieces', e.target.value)}
                    className={styles.input}
                  />
                </div>
                <div className={styles.formGroup}>
                  <label>Weight:</label>
                  <input
                    type="text"
                    inputMode="numeric"
                    value={formData.weight || ''}
                    onChange={(e) => handleInputChange('weight', e.target.value)}
                    className={styles.input}
                  />
                </div>
                <div className={styles.formGroup}>
                  <label>Dimensions:</label>
                  <input
                    type="text"
                    value={formData.dimensions || ''}
                    onChange={(e) => handleInputChange('dimensions', e.target.value)}
                    className={styles.input}
                  />
                </div>
                <div className={styles.formGroup}>
                  <label>Requirements:</label>
                  <input
                    type="text"
                    value={formData.special_equipment || ''}
                    onChange={(e) => handleInputChange('special_equipment', e.target.value)}
                    className={styles.input}
                    placeholder="Паллет джек, док хай и т.д."
                  />
                </div>
              </div>
              <div className={styles.column}>
                <div className={styles.formGroup}>
                  <label>Total Miles:</label>
                  <input
                    type="text"
                    inputMode="numeric"
                    value={formData.total_miles || ''}
                    onChange={(e) => handleInputChange('total_miles', e.target.value)}
                    className={styles.input}
                  />
                </div>
                <div className={styles.formGroup}>
                  <label>Empty Miles:</label>
                  <input
                    type="text"
                    inputMode="numeric"
                    value={formData.empty_miles || ''}
                    onChange={(e) => handleInputChange('empty_miles', e.target.value)}
                    className={styles.input}
                    placeholder="Optional"
                  />
                </div>
              </div>
              <div className={styles.column}>
                <div className={styles.formGroup}>
                  <label>Dispatcher Name:</label>
                  <input
                    type="text"
                    value={formData.dispatcher_name || ''}
                    onChange={(e) => handleInputChange('dispatcher_name', e.target.value)}
                    className={styles.input}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className={styles.footer}>
        <button
          className={styles.closeBtn}
          onClick={handleCall}
          disabled={callLoading}
        >
          {callLoading ? 'Calling...' : 'Call'}
        </button>
      </div>
    </>
  );
};

export default AICallPreviewModal;